import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Activity, Zap, TrendingUp, Target, BarChart3, Code, Download } from 'lucide-react';
import AnimatedSection from '../../components/AnimatedSection';

const AlgorithmPage = () => {
  const features = [
    {
      icon: <Code size={32} />,
      title: 'Advanced Execution',
      description: 'Ultra-low latency algorithm execution with direct market access for optimal order placement'
    },
    {
      icon: <Target size={32} />,
      title: 'Precision Backtesting',
      description: 'Test your strategies against historical data with tick-by-tick accuracy and comprehensive performance metrics'
    },
    {
      icon: <Activity size={32} />,
      title: 'Real-Time Analytics',
      description: 'Monitor algorithm performance in real-time with detailed analytics and risk management tools'
    }
  ];

  return (
    <div className="min-h-screen pt-24">
      {/* Hero Section */}
      <section className="py-24 bg-gradient-to-br from-[#0B0F1A] via-[#1a1147] to-[#0B0F1A] relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-[#7C3AED]/10 via-transparent to-[#00D4FF]/10" />
        
        {/* Animated Algorithm Visualization */}
        <div className="absolute inset-0 overflow-hidden opacity-20">
          {[...Array(8)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-2 h-2 bg-[#00D4FF] rounded-full"
              style={{
                left: `${10 + i * 12}%`,
                top: '50%'
              }}
              animate={{
                y: [0, -50, 0],
                opacity: [0.3, 1, 0.3],
                scale: [1, 1.5, 1]
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                delay: i * 0.2
              }}
            />
          ))}
        </div>
        
        <div className="relative z-10 max-w-[1280px] mx-auto px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-6xl md:text-7xl font-bold mb-6">
              Professional Trading
              <br />
              <span className="text-[#7C3AED]">Algorithm</span>
            </h1>
            <p className="text-xl text-[#9CA3AF] max-w-3xl mx-auto">
              Automate your trading strategy with our enterprise-grade algorithm infrastructure and millisecond execution
            </p>
          </motion.div>
        </div>
      </section>

      {/* Download Section - In the middle */}
      <section className="py-24 bg-[#0B0F1A]">
        <div className="max-w-[1280px] mx-auto px-8">
          <AnimatedSection>
            <div className="bg-gradient-to-br from-[#7C3AED]/10 to-[#00D4FF]/10 border border-white/10 rounded-3xl p-12 text-center relative overflow-hidden flex flex-col lg:flex-row items-center gap-12">
              <div className="relative z-10 flex-1 text-left lg:pl-8">
                <h2 className="text-4xl font-bold mb-6">Download Our Premium MT5 Algorithm</h2>
                <p className="text-xl text-[#9CA3AF] mb-10 max-w-2xl">
                  Get instant access to our advanced MT5 trading algorithm. Engineered for MetaTrader 5 with millisecond precision and robust execution.
                </p>
                
                <div className="flex flex-col sm:flex-row items-start gap-6">
                  <Link to="/signup">
                    <motion.button
                      className="bg-[#7C3AED] text-white px-10 py-4 rounded-xl font-bold text-lg flex items-center gap-3 hover:shadow-2xl hover:shadow-[#7C3AED]/50 transition-all"
                      whileHover={{ scale: 1.05, translateY: -4 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <Download size={24} /> Download for MT5
                    </motion.button>
                  </Link>
                </div>
                
                <p className="mt-8 text-sm text-[#9CA3AF]">
                  Compatible with MetaTrader 5 (MT5) Desktop
                </p>
              </div>

              {/* 3D Animation Section */}
              <div className="flex-1 flex items-center justify-center perspective-1000">
                <motion.div
                  className="w-64 h-64 bg-gradient-to-br from-[#00D4FF] to-[#7C3AED] rounded-2xl shadow-2xl relative flex items-center justify-center"
                  animate={{
                    rotateY: [360, 0],
                    rotateX: [-10, 10, -10],
                    y: [0, 20, 0]
                  }}
                  transition={{
                    duration: 10,
                    repeat: Infinity,
                    ease: "linear"
                  }}
                  style={{
                    transformStyle: "preserve-3d"
                  }}
                >
                  <div className="absolute inset-0 bg-white/10 backdrop-blur-sm rounded-2xl" />
                  <div className="relative z-20 flex flex-col items-center">
                    <Zap size={80} className="text-white mb-4" />
                    <span className="text-white font-bold text-2xl">MT5</span>
                    <span className="text-white/80 text-sm">ALGORITHM</span>
                  </div>
                  
                  {/* Floating elements for 3D depth */}
                  <motion.div 
                    className="absolute -bottom-4 -left-4 w-12 h-12 bg-[#7C3AED] rounded-lg flex items-center justify-center shadow-lg"
                    animate={{ translateZ: [30, 60, 30] }}
                    transition={{ duration: 5, repeat: Infinity }}
                  >
                    <Target size={24} className="text-white" />
                  </motion.div>
                </motion.div>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-24 bg-[#0F172A]">
        <div className="max-w-[1280px] mx-auto px-8">
          <AnimatedSection>
            <h2 className="text-4xl font-bold text-center mb-16">Algorithm Capabilities</h2>
          </AnimatedSection>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <AnimatedSection key={index} delay={index * 0.1}>
                <motion.div
                  className="bg-white/5 backdrop-blur-lg border border-white/10 rounded-2xl p-8 hover:border-[#7C3AED]/50 transition-all"
                  whileHover={{ scale: 1.05, translateY: -8 }}
                >
                  <div className="text-[#7C3AED] mb-4">
                    {feature.icon}
                  </div>
                  <h3 className="text-2xl font-bold mb-3">{feature.title}</h3>
                  <p className="text-[#9CA3AF]">{feature.description}</p>
                </motion.div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-[#7C3AED]/20 via-[#ec4899]/10 to-[#00D4FF]/20">
        <div className="max-w-[1280px] mx-auto px-8 text-center">
          <AnimatedSection>
            <h2 className="text-4xl font-bold mb-6">Ready to Automate Your Trading?</h2>
            <p className="text-xl text-[#9CA3AF] mb-8 max-w-2xl mx-auto">
              Start deploying our professional algorithm today
            </p>
            <Link to="/signup">
              <motion.button
                className="bg-white text-[#0B0F1A] px-10 py-4 rounded-lg font-bold text-lg hover:shadow-2xl hover:shadow-white/30 transition-all"
                whileHover={{ scale: 1.05, translateY: -4 }}
                whileTap={{ scale: 0.98 }}
              >
                Get Started
              </motion.button>
            </Link>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
};

export default AlgorithmPage;
